import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import {
  FILE_OBJECT_TYPE_INFO_REQUEST_INVOICE,
  type BackofficeInformationRequestCard,
  type InformationRequestInvoiceListByInformationRequestIdItem
} from 'types/information-request.interface';
import { emailValidator } from 'util/form.utility';

const MAX_BODY_LENGTH = 2000;

export interface InvoiceSendEmailPayload {
  recipient: string;
  emailSubject: string;
  emailBody: string;
}

@Component({
  selector: 'app-ir-invoice-send-email-modal',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, TranslateModule, FileUploadComponent, TooltipComponent],
  templateUrl: './send-email-modal.component.html'
})
export class IrInvoiceSendEmailModalComponent implements OnChanges {
  readonly MAX_BODY_LENGTH = MAX_BODY_LENGTH;
  readonly fileTypeInvoice = FILE_OBJECT_TYPE_INFO_REQUEST_INVOICE;
  @Input() isOpen = false;
  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Input() invoice: InformationRequestInvoiceListByInformationRequestIdItem | null = null;
  @Input() isLoading = false;
  @Output() confirmed = new EventEmitter<InvoiceSendEmailPayload>();
  @Output() closed = new EventEmitter<void>();
  @ViewChild('content') content!: TemplateRef<unknown>;

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private translateService: TranslateService
  ) {
    this.form = this.fb.group({
      recipient: ['', [Validators.required, emailValidator()]],
      emailSubject: ['', Validators.required],
      emailBody: ['', [Validators.required, Validators.maxLength(MAX_BODY_LENGTH)]]
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) return;
    if (this.isOpen) {
      this.fillForm();
      this.modalService.open(this.content, { backdrop: 'static', keyboard: false, windowClass: 'send-email-modal' });
    } else {
      this.modalService.dismissAll();
    }
  }

  private fillForm(): void {
    const req = this.informationRequest;
    const recipient = this.resolveDefaultRecipient();
    const code = req?.code || '';
    // LNA-2064: the invoice has no number of its own, and "{code}-{ordinalNumber}" read as one.
    // The subject names the request instead, like the body and the response e-mail do.
    const subject = this.translateService.instant('pages.viewInfoRequest.invoice.sendEmailModal.subjectDefault', { code });
    const body = this.buildDefaultBody(code, this.resolveUnitContactInfo());
    this.form.reset({ recipient, emailSubject: subject, emailBody: body });
  }

  // Mirrors the response send-email template (patchSendEmailForm on the IR card page):
  // bilingual LV + "---" + EN body with the responsible unit's contact info in both halves.
  private buildDefaultBody(code: string, unitInfo: string): string {
    const lvBody = `Labdien!\nNosūtām rēķinu (skat. pielikumā) par pieprasījumu Nr. ${code}.\nPaldies, ka izmantojat mūsu pakalpojumus!\nCieņā\nLatvijas Nacionālais arhīvs\n${unitInfo}\nŠis ir automātiski ģenerēts ziņojums, lūdzam neatbildēt.`;
    const enBody = `Dear Sir/Madam,\nPlease find attached the invoice for request No. ${code}.\nThank you for using our services!\nYours sincerely,\nNational Archives of Latvia\n${unitInfo}\nThis is an automatically generated message, please do not reply.`;
    return `${lvBody}\n\n---\n\n${enBody}`;
  }

  private resolveUnitContactInfo(): string {
    const unit = this.informationRequest?.responsibleStructuralUnit;
    if (unit) {
      return this.buildUnitContactInfo(unit.name, unit.phone, unit.email);
    }
    return this.informationRequest?.responsibleStructuralUnitName || '';
  }

  private buildUnitContactInfo(name: string, phone?: string | null, email?: string | null): string {
    const parts = [name];
    if (phone) parts.push(phone);
    if (email) parts.push(email);
    return parts.join(', ');
  }

  private resolveDefaultRecipient(): string {
    const req = this.informationRequest;
    if (!req) return '';
    if (req.invoiceDeliveryMethod === 'Email' && req.invoiceDeliveryDetail) {
      return req.invoiceDeliveryDetail;
    }
    return req.contactPersonEmail || req.legalEntity?.email || req.client?.email || '';
  }

  confirm(): void {
    if (this.form.invalid || this.isLoading) return;
    this.confirmed.emit(this.form.getRawValue() as InvoiceSendEmailPayload);
  }

  close(): void {
    this.modalService.dismissAll();
    this.closed.emit();
  }
}
